import React, { useRef } from "react";
import { Menu, Dropdown } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { toJpeg, toCanvas } from "html-to-image";
import { jsPDF } from "jspdf";

import {
  Container,
  ContentPadding,
  Title,
  LegendContainer,
  Legend,
  LegendTitle,
  LegendGroup,
  FunnelGraphContainer,
  PassedDiv,
  FailedDiv,
  SplitGraphWrapper,
  GraphTitle,
  FunnelGraphSplitContainer,
  DropdownWrapper
} from "./element";
import numberAbbr from "../../../utils/helpers/numberAbbr";
import { Loader } from "../../atoms/Loader";

type Props = {
  onClick: (e: any) => void | (() => void);
  data: {
    applicants: {
      passed: number;
      failed: number;
    };
    shortlisted: {
      passed: number;
      failed: number;
    };
    technical: {
      passed: number;
      failed: number;
    };
    initialInterview: {
      passed: number;
      failed: number;
    };
    psychological: {
      passed: number;
      failed: number;
    };
    finalInterview: {
      passed: number;
      failed: number;
    };
  };
  isLoading?: boolean;
  dateRange?: any;
};

const RecruitmentFunnel = (props: Props) => {
  const { onClick, data, isLoading, dateRange } = props;
  const csvRef = useRef<any>();
  const jpgRef = useRef<any>();

  const handleClickMenu = (e: any) => {
    switch (e.key) {
      case "CSV":
        csvRef.current?.link.click();
        break;
      case "JPG":
        toJpeg(jpgRef.current, { cacheBust: true })
          .then(url => {
            const link = document.createElement("a");
            link.download = `recruitmentFunnelInsight.jpg`;
            link.href = url;
            link.click();
          })
          .catch(err => {
            console.log(err);
          });
        break;
      default:
        toCanvas(jpgRef.current).then(canvas => {
          const image = canvas.toDataURL("image/jpeg", 1.0);
          const doc = new jsPDF("p", "px", "a4");
          doc.addImage(image, "JPEG", 10, 10, 0, 0);
          doc.save(`recruitmentFunnelInsight.pdf`);
        });
        break;
    }
  };

  const dropdownMenu = (
    <Menu onClick={handleClickMenu}>
      <Menu.Item key="JPG">Download JPG</Menu.Item>
      <Menu.Item key="PDF">Download PDF</Menu.Item>
      <Menu.Item key="CSV">Download CSV</Menu.Item>
    </Menu>
  );

  const valueAllocator = (name: string) => {
    switch (name) {
      case "applicants":
        return {
          name: "Number of Applies",
          width: "2%"
        };
      case "shortlisted":
        return {
          name: "Shortlisted",
          width: "6%"
        };
      case "technical":
        return {
          name: "Technical Exam",
          width: "10%"
        };
      case "initialInterview":
        return {
          name: "Initial Interview",
          width: "14%"
        };
      case "psychological":
        return {
          name: "Psychological Exam",
          width: "18%"
        };
      case "finalInterview":
        return {
          name: "Final Interview",
          width: "21%"
        };
    }
  };

  return (
    <div style={{ height: "100%" }} ref={jpgRef}>
      <Container>
        <ContentPadding>
          <DropdownWrapper>
            <Dropdown
              trigger={["click"]}
              overlay={dropdownMenu}
              placement="bottomLeft"
            >
              <MoreOutlined
                rotate={90}
                style={{ fontSize: "26px", color: "#000000" }}
              />
            </Dropdown>
          </DropdownWrapper>
          <Title>Active Recruitment Funnel</Title>
          <LegendContainer>
            <LegendGroup>
              <Legend passed />
              <LegendTitle>Passed</LegendTitle>
            </LegendGroup>
            <LegendGroup>
              <Legend passed={false} />
              <LegendTitle>Failed</LegendTitle>
            </LegendGroup>
          </LegendContainer>
          <FunnelGraphContainer
            isLoading={isLoading}
            style={{ fontWeight: 400 }}
          >
            {isLoading ? (
              <Loader />
            ) : (
              <>
                {Object.entries(data).map(([key, values]) => {
                  const assignable = valueAllocator(key);
                  return (
                    <FunnelGraphSplitContainer key={key}>
                      <SplitGraphWrapper marginLR={assignable?.width}>
                        <PassedDiv>{numberAbbr(values.passed)}</PassedDiv>
                        <FailedDiv>{numberAbbr(values.failed)}</FailedDiv>
                      </SplitGraphWrapper>
                      <GraphTitle>{assignable?.name}</GraphTitle>
                    </FunnelGraphSplitContainer>
                  );
                })}
              </>
            )}
          </FunnelGraphContainer>
        </ContentPadding>
        <CSVLink
          style={{ visibility: "hidden" }}
          data={[
            {
              firstRow: "Date Range: ",
              secondRow: `${dateRange[0].format(
                "YYYY-MM-DD"
              )} - ${dateRange[1].format("YYYY-MM-DD")}`,
              thirdRow: ""
            },
            {},
            {
              firstRow: "Number of Applies: Passed",
              secondRow: "Number of Applies: Failed"
            },
            {
              firstRow: data.applicants.passed,
              secondRow: data.applicants.failed
            },
            {
              firstRow: "Shortlisted: Passed",
              secondRow: "Shortlisted: Failed"
            },
            {
              firstRow: data.shortlisted.passed,
              secondRow: data.shortlisted.failed
            },
            {
              firstRow: "Technical Exam: Passed",
              secondRow: "Technical Exam: Failed"
            },
            {
              firstRow: data.technical.passed,
              secondRow: data.technical.failed
            },
            {
              firstRow: "Initial Interview: Passed",
              secondRow: "Initial Interview: Failed"
            },
            {
              firstRow: data.initialInterview.passed,
              secondRow: data.initialInterview.failed
            },
            {
              firstRow: "Psychological Exam: Passed",
              secondRow: "Psychological Exam: Failed"
            },
            {
              firstRow: data.psychological.passed,
              secondRow: data.psychological.failed
            },
            {
              firstRow: "Final Interview: Passed",
              secondRow: "Final Interview: Failed"
            },
            {
              firstRow: data.finalInterview.passed,
              secondRow: data.finalInterview.passed
            }
          ]}
          headers={[
            { key: "firstRow", label: "" },
            { key: "secondRow", label: "" },
            { key: "thirdRow", label: "" }
          ]}
          filename="Recruitment Funnel Insight.csv"
          ref={csvRef}
          target="_blank"
        ></CSVLink>
      </Container>
    </div>
  );
};

export default RecruitmentFunnel;
